/**
 * The user picks light, dark or the theme of the operating system in the user menu. Local
 * storage keeps the pick for this browser, under its own key: the reset of a broken workspace
 * clears the `vms.panels.` keys and keeps the theme.
 */

import { atom } from 'jotai';
import { atomWithLazy, atomWithStorage } from 'jotai/utils';

import { jsonStorage } from '../storage';
import { localStore } from '@/lib/webStorage';
import type { Unsubscribe } from '@/types/common';
import { Theme, ThemePreference } from '@/types/panels';

const STORAGE_KEY = 'occurra.themePreference';
const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';
const THEME_PREFERENCES: readonly string[] = Object.values(ThemePreference);

function parseThemePreference(storedValue: unknown): ThemePreference {
    return typeof storedValue === 'string' && THEME_PREFERENCES.includes(storedValue)
        ? storedValue as ThemePreference : ThemePreference.System;
}

const storedThemePreferenceAtom = atomWithStorage<ThemePreference>(
    STORAGE_KEY, ThemePreference.System, jsonStorage(localStore, parseThemePreference),
    { getOnInit: true },
);

function getSystemTheme(): Theme {
    return window.matchMedia(DARK_SCHEME_QUERY).matches ? Theme.Dark : Theme.Light;
}

const systemThemeAtom = atomWithLazy<Theme>(getSystemTheme);
systemThemeAtom.onMount = (setSystemTheme): Unsubscribe => {
    const query = window.matchMedia(DARK_SCHEME_QUERY);
    const onChange = (event: MediaQueryListEvent): void => {
        setSystemTheme(event.matches ? Theme.Dark : Theme.Light);
    };
    query.addEventListener('change', onChange);
    return (): void => query.removeEventListener('change', onChange);
};

export const themePreferenceAtom = atom(
    (get): ThemePreference => get(storedThemePreferenceAtom),
);

export const setThemePreferenceAtom = atom(
    null, (get, set, themePreference: ThemePreference): void => {
        if (get(storedThemePreferenceAtom) !== themePreference) {
            set(storedThemePreferenceAtom, themePreference);
        }
    });

export const themeAtom = atom((get): Theme => {
    const systemTheme = get(systemThemeAtom);
    const themePreference = get(themePreferenceAtom);
    return themePreference === ThemePreference.System ? systemTheme : themePreference;
});
